import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { pageContentsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Save, Plus, Trash2, Star, GripVertical } from 'lucide-react';

const defaultTestimonials = [
  { name: "Carlos Eduardo Silva", role: "Proprietário - Oficina Hidráulica Maquinatech", location: "Ribeirão Preto - SP", text: "A TerraHydro resolveu um problema crítico que tínhamos há semanas.", rating: 5 },
  { name: "Roberto Martins", role: "Gerente de Manutenção", location: "Goiânia - GO", text: "Trabalho com hidráulica há 20 anos e a TerraHydro é referência.", rating: 5 },
  { name: "Ana Paula Costa", role: "Engenheira Mecânica - AgroSul Equipamentos", location: "Uberlândia - MG", text: "O suporte técnico é diferenciado.", rating: 5 }
];

const AdminTestimonials = () => {
  const [title, setTitle] = useState('O Que Dizem Nossos Clientes');
  const [subtitle, setSubtitle] = useState('Confiança construída através de resultados e relacionamentos duradouros');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const data = await pageContentsAPI.get('testimonials');
      setTitle(data.title || 'O Que Dizem Nossos Clientes');
      setSubtitle(data.subtitle || '');
      setItems(data.metadata?.items || defaultTestimonials);
    } catch {
      setItems(defaultTestimonials);
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const addItem = () => {
    setItems(prev => [...prev, { name: '', role: '', location: '', text: '', rating: 5 }]);
  };

  const removeItem = (index) => {
    if (window.confirm('Remover este depoimento?')) {
      setItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await pageContentsAPI.update('testimonials', {
        title,
        subtitle,
        metadata: { items }
      });
      alert('Depoimentos salvos com sucesso!');
    } catch (error) {
      alert('Erro ao salvar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 data-testid="testimonials-page-title" className="text-2xl font-bold text-gray-800">Depoimentos</h1>
            <p className="text-gray-600">Gerencie os depoimentos exibidos no site</p>
          </div>
          <Button data-testid="save-testimonials-btn" onClick={handleSave} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving}>
            {saving ? 'Salvando...' : <><Save size={16} className="mr-2" />Salvar Todos</>}
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader><CardTitle>Configurações da Seção</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título da Seção</label>
              <Input data-testid="testimonials-section-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Título" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo da Seção</label>
              <Input data-testid="testimonials-section-subtitle" value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Subtítulo" />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {items.map((item, index) => (
            <Card key={index} data-testid={`testimonial-card-${index}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GripVertical size={18} className="text-gray-400" />
                    <CardTitle className="text-base">Depoimento #{index + 1}</CardTitle>
                  </div>
                  <Button data-testid={`remove-testimonial-${index}`} variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <Input data-testid={`testimonial-name-${index}`} value={item.name} onChange={e => updateItem(index, 'name', e.target.value)} placeholder="Nome completo" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cargo / Empresa</label>
                    <Input data-testid={`testimonial-role-${index}`} value={item.role} onChange={e => updateItem(index, 'role', e.target.value)} placeholder="Ex: Gerente de Manutenção - Empresa X" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Localização</label>
                    <Input data-testid={`testimonial-location-${index}`} value={item.location} onChange={e => updateItem(index, 'location', e.target.value)} placeholder="Ex: Goiânia - GO" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Avaliação</label>
                    <div className="flex gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button key={star} type="button" data-testid={`testimonial-star-${index}-${star}`} onClick={() => updateItem(index, 'rating', star)} className="p-1 hover:scale-110 transition-transform">
                          <Star size={24} className={star <= (item.rating || 5) ? 'fill-[#FFB025] text-[#FFB025]' : 'text-gray-300'} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Depoimento</label>
                  <textarea data-testid={`testimonial-text-${index}`} value={item.text} onChange={e => updateItem(index, 'text', e.target.value)} className="w-full border rounded-md px-3 py-2 text-sm min-h-[100px]" placeholder="Texto do depoimento..." />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-6 flex justify-between">
          <Button data-testid="add-testimonial-btn" variant="outline" onClick={addItem} className="border-[#055509] text-[#055509] hover:bg-[#055509]/5">
            <Plus size={16} className="mr-2" />Adicionar Depoimento
          </Button>
          <Button data-testid="save-testimonials-bottom-btn" onClick={handleSave} className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving}>
            {saving ? 'Salvando...' : <><Save size={16} className="mr-2" />Salvar Todos</>}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTestimonials;
